import { dirname, join, resolve } from "node:path";
import { attempt } from "@kidd-cli/utils/fp";
import { existsSync } from "node:fs";
import { homedir } from "node:os";
import { match as match$1 } from "ts-pattern";
//#region src/lib/project/root.ts
/**
* Walk up the directory tree to find the nearest git project root.
*
* Stops at the first directory containing a `.git` entry (either a `.git`
* directory in a normal clone, or a `.git` file in a worktree or submodule).
*
* @param startDir - Directory to start searching from (defaults to cwd).
* @returns The absolute path of the project root, or null if none is found.
*/
function findProjectRoot(startDir = process.cwd()) {
	const findRootRecursive = (currentDir, visited) => {
		if (visited.has(currentDir)) return null;
		const nextVisited = new Set([...visited, currentDir]);
		const gitPath = join(currentDir, ".git");
		const [, exists] = attempt(() => existsSync(gitPath));
		if (exists) return currentDir;
		const parent = dirname(currentDir);
		if (parent === currentDir) return null;
		return findRootRecursive(parent, nextVisited);
	};
	return findRootRecursive(resolve(startDir), /* @__PURE__ */ new Set());
}
//#endregion
//#region src/lib/project/paths.ts
/**
* Resolve a directory path relative to the project root.
*
* @param options - Options containing the directory name and optional start directory.
* @returns The resolved local path, or null if no project root is found.
*/
function resolveLocalPath(options) {
	const projectRoot = findProjectRoot(options.startDir);
	if (!projectRoot) return null;
	return join(projectRoot, options.dirName);
}
/**
* Resolve a directory path relative to the user's home directory.
*
* @param options - Options containing the directory name.
* @returns The resolved global path.
*/
function resolveGlobalPath(options) {
	return join(homedir(), options.dirName);
}
/**
* Resolve a directory path using the specified source strategy.
*
* When source is 'local', resolves relative to the project root.
* When source is 'global', resolves relative to the home directory.
* When source is 'resolve' (default), tries local first, falling back to global.
*
* @param options - Resolution options with dirName, source, and startDir.
* @returns The resolved path, or null if local resolution fails with source='local'.
*/
function resolvePath(options) {
	const { dirName, source = "resolve", startDir } = options;
	return match$1(source).with("local", () => resolveLocalPath({
		dirName,
		startDir
	})).with("global", () => resolveGlobalPath({ dirName })).with("resolve", () => {
		const localPath = resolveLocalPath({
			dirName,
			startDir
		});
		if (localPath) return localPath;
		return resolveGlobalPath({ dirName });
	}).exhaustive();
}
//#endregion
export { findProjectRoot as i, resolveLocalPath as n, resolvePath as r, resolveGlobalPath as t };

//# sourceMappingURL=paths-Clz-xMIL.js.map